import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Http, URLSearchParams, Response } from '@angular/http';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/combineAll';
import 'rxjs/add/operator/skip';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import * as moment from 'moment';
import { ToastsManager } from 'ng2-toastr/ng2-toastr';

import { Configuration, MatchType } from './configuration.model';
import { Match } from './match.model';
import { League } from './league.model';
import { Team } from './team.model';
import { Player } from './player.model';
import { Position } from './position.model';
import { Lineup } from './lineup.model';
import { Trophie } from './trophie.model';
import { Transfer } from './transfer.model';
import { Injury } from './injury.model';
import { TeamStatistic } from './team-statistic.model';
import { PlayerStatistic} from './player-statistic.model';
import { MissingPlayer } from './missing-player.model';
import { Penalty } from './penalty.model';
import { PenaltyStatistic } from './penalty-statistic.model';
import { Standing } from './standing.model';
import { Clock } from './clock.model';
import { LivePlayerStatistic } from './live-player-statistic.model';
import { Score } from './score.model';
import { LastLineupMatch } from './last-lineup-match.model';
import { Referee } from './referee.model';
import {isNullOrUndefined} from 'util';

export type DataDirection = 'next' | 'last';

export const DataDirection = { // Workaround until TypeScript 2.4 can be used and strings in enums are supported
    Next: 'next' as DataDirection,
    Last: 'last' as DataDirection
}

@Injectable()
export class DataService {
    private base = 'https://api.comtool.tv/';
    //private base = 'http://83.169.21.107/';
    //private apiBase = this.base + 'ComToolRest/api/v1/';
    private apiBase = this.base + 'v1/';
    private soundFileBase = 'https://comtool.tv/';
    private token = '809E1464B82F49457716348F07EEC0411053929ECD7F7A06AD701A857CC673BA';

    userId: string;
    configuration: Configuration;

    constructor(
        private activatedRoute: ActivatedRoute,
        private http: Http,
        private toastsManager: ToastsManager
    ) {
        this.activatedRoute.queryParams
            .subscribe( // Will be unsubscribed from by Angular itself
                (params: Params) => {
                    this.configuration = new Configuration();
                    this.configuration.matchId = +params['matchid'];
                    switch (params['matchtype']) {
                        case 'LIVE': {
                            this.configuration.matchType = MatchType.Live;
                            break;
                        }
                        case 'MANUAL': {
                            this.configuration.matchType = MatchType.Manual;
                            break;
                        }
                    }

                    this.configuration.league = +params['leagueid'];
                    this.configuration.homeTeam = +params['teamhomeid'];
                    this.configuration.awayTeam = +params['teamvisitorid'];
                    this.configuration.isNational = !!+params['nt'];
                    this.userId = params['userid'];
                }
            );
    }

    isInitialized() {
        return this.configuration instanceof Configuration && this.configuration.isValid() && typeof this.userId !== 'undefined';
    }

    private extractData(response: Response) {
        return response.text() ? response.json() : {};
    }

    private handleError(error: Response | any) {
        let message: string;
        if (error instanceof Response) {
            message = `${error.status} - ${error.statusText || 'Unknown'}`;
        } else {
            message = error.message ? error.message : error.toString();
        }

        this.toastsManager.error('An error occured! Failed to fetch data.', 'Error!');
        return Observable.throw(message);
    }

    getLeagues() {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'leagues', { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const leagues = [];
                    for (const data of json) {
                        const league = new League();
                        league.id = +data.id;
                        league.leagueId = +data.leagueID;
                        league.country = data.country;
                        league.description = data.description;
                        league.name = data.name;
                        leagues.push(league);
                    }

                    return leagues;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getTeams(league: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'teams/' + league, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const teams = [];
                    for (const data of json) {
                        const team = new Team();
                        team.id = +data.id;
                        team.leagueId = +data.leagueID;
                        team.country = data.country;
                        team.description = data.description;
                        team.name = data.name;
                        team.teamId = +data.teamID;
                        teams.push(team);
                    }

                    return teams;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getPlayers(match: number, team: number) {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'players/' + match + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const players = [];
                    for (const data of json) {
                        const player = new Player();
                        player.id = +data.id;
                        player.number = +data.number === -1 ? 0 : +data.number;
                        player.country = data.country;
                        if (player.country === 'Bosnia and Herzegovina') {
                            player.country = 'Bosnia a. H.';
                        } else if (player.country === 'Republic of Ireland') {
                            player.country = 'Rep. Ireland';
                        }

                        player.description = data.description;
                        player.position = data.position;
                        player.birthDate = moment(data.birthDate, 'DD/MM/YYYY');
                        player.birthDay = +player.birthDate.format('D');
                        player.birthMonth = +player.birthDate.format('M');
                        player.birthYear = +player.birthDate.format('YYYY');
                        player.firstName = data.firstName;
                        player.lastName = data.lastName;
                        player.nationality = data.nationality;
                        player.nationalMatches = +data.matchesNT;
                        player.nationalGoals = +data.goalsNT;
                        player.birthCountry = data.birthCountry;
                        player.weight = data.weight;
                        player.teamId = +data.teamID;
                        player.teamName = data.clubTeamName;
                        player.birthPlace = data.birthPlace;
                        player.seasonGoals = +data.goals;
                        player.playerId = +data.playerID;
                        player.name = data.playerName;
                        player.age = +data.age;
                        player.height = data.height;
                        player.keyfacts = data.keyFacts;
                        player.x = +data.posX;
                        player.y = +data.posY;
                        player.pitchWidth = +data.pitchWidth;
                        player.pitchHeight = +data.pitchHeight;
                        players.push(player);
                    }

                    return players;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getPlayersLegacy(team: number) {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'players/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const players = [];
                    for (const data of json) {
                        const player = new Player();
                        player.id = +data.id;
                        player.number = +data.number;
                        player.country = data.country;
                        player.description = data.description;
                        player.position = data.position;
                        player.birthDate = moment(data.birthDate, 'DD/MM/YYYY');
                        player.firstName = data.firstName;
                        player.lastName = data.lastName;
                        player.nationality = data.nationality;
                        player.birthCountry = data.birthCountry;
                        player.weight = data.weight;
                        player.teamId = +data.teamID;
                        player.birthPlace = data.birthPlace;
                        player.playerId = +data.playerID;
                        player.age = +data.age;
                        player.height = data.height;
                        player.keyfacts = data.keyfacts;
                        players.push(player);
                    }

                    return players;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getMatches(league: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'matches/' + league, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const matches = [];
                    for (const data of json) {
                        const match = new Match();
                        match.id = +data.id;
                        match.location = data.location;
                        match.country = data.country;
                        match.description = data.description;
                        match.stage = +data.stage;
                        match.awayTeamScore = +data.teamVisitorScore;
                        match.leagueId = +data.leagueID;
                        match.matchId = +data.matchID;
                        match.awayTeamId = +data.teamVisitorID;
                        match.staticId = +data.matchStaticID;
                        match.homeTeamScore = +data.teamHomeScore;
                        match.matchDate = moment(data.matchDate);
                        match.halftimeScore = data.halftimeScore;
                        match.season = data.season;
                        match.homeTeamId = +data.teamHomeID;
                        match.city = data.city;
                        matches.push(match);
                    }

                    return matches;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getTeamMatches(team: number, dataDirection: DataDirection, num: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'matches/team/' + team + '/' + dataDirection + '/' + num, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const matches = [];
                    for (const data of json) {
                        const match = new Match();
                        match.id = +data.id;
                        match.location = data.location;
                        match.country = data.country;
                        match.description = data.description;
                        match.stage = +data.stage;
                        match.awayTeamScore = +data.teamVisitorScore;
                        match.leagueId = +data.leagueID;
                        match.leagueName = data.leagueName;
                        match.matchId = +data.matchID;
                        match.awayTeamId = +data.teamVisitorID;
                        match.awayTeamName = data.teamVisitorName;
                        match.staticId = +data.matchStaticID;
                        match.homeTeamScore = +data.teamHomeScore;
                        match.matchDate = moment(data.matchDate, 'DD.MM.YYYY');
                        match.halftimeScore = data.halftimeScore;
                        match.season = data.season;
                        match.homeTeamId = +data.teamHomeID;
                        match.homeTeamName = data.teamHomeName;
                        match.city = data.city;
                        matches.push(match);
                    }

                    return matches;
                }
            )
            .catch(this.handleError.bind(this));
    }

	/*
    getLastLineup(match: number, team: number) {
        let params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'last_lineup/' + match + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    let lineup = new Lineup();
                    if(typeof json[0] !== 'undefined') {
                        lineup.formation = json[0].formation;
                    }

                    for(const data of json) {
                        let position = new Position();
                        position.player = +data.playerID;
                        position.position = +data.formationPosition.substring(3, data.formationPosition.length);
                        position.substitution = data.substitution.toLowerCase() === 'true';
                        lineup.positions.push(position);
                    }

                    return lineup;
                }
            )
            .catch(this.handleError.bind(this));
    }
	*/

    getLastLineup(match: number, team: number) {
        const params = new URLSearchParams();
        // params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/last_lineup/' + match + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const lineup = new Lineup();
                    if (typeof json[0] !== 'undefined') {
                        lineup.formation = json[0].formation;
                    }
                    for (const data of json) {
                        let position = new Position();
                        position.player = +data.playerID;
                        position.position = +data.formationPosition;
                        // position.position = +data.formationPosition.substring(3, data.formationPosition.length);
                        position.substitution = data.substitution.toLowerCase() === 'true';
                        lineup.positions.push(position);
                    }

                    return lineup;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getLastLineupMatch(match: number, team: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'last_lineup/match/' + match + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    if (typeof json.score === 'undefined') {
                        return null;
                    }

                    const scores = json.score.split(':');
                    if (scores.length !== 2) {
                        return null;
                    }

                    const lastLineupMatch = new LastLineupMatch();
                    lastLineupMatch.awayTeamId = +json.teamVisitorID;
                    lastLineupMatch.awayTeamName = json.teamVisitorName;
                    lastLineupMatch.awayTeamScore = +scores[1];
                    lastLineupMatch.homeTeamId = +json.teamHomeID;
                    lastLineupMatch.homeTeamName = json.teamHomeName;
                    lastLineupMatch.homeTeamScore = +scores[0];
                    lastLineupMatch.matchDate = moment(json.matchDate, 'DD.MM.YYYY');
                    return lastLineupMatch;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getTrophies(player: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'playertrophies/' + player, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const trophies = [];
                    for (const data of json) {
                        const trophie = new Trophie();
                        trophie.id = +data.id;
                        trophie.country = data.country;
                        trophie.count = +data.count;
                        trophie.playerId = +data.playerID;
                        trophie.leagueName = data.leagueName;
                      trophie.teamname = data.teamName;
                      trophie.as_coach = data.asCoach
                      trophie.seasons = data.seasons;
                        trophie.ranking = data.ranking;
                        trophies.push(trophie);
                    }

                    return trophies;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getTransfers(player: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'playertransfers/' + player, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const transfers = [];
                    for (const data of json) {
                        const transfer = new Transfer();
                        transfer.id = +data.id;
                        transfer.date = moment(data.transferDate, 'DD.MM.YYYY');
                        transfer.from = data.transferFrom;
                        transfer.to = data.transferTo;
                        transfer.value = data.transferValue;
                        transfer.playerId = +data.playerID;
                        transfers.push(transfer);
                    }

                    return transfers;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getInjuries(player: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'playerinjuries/' + player, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const injuries = [];
                    for (const data of json) {
                        const injury = new Injury();
                        injury.id = +data.id;
                        injury.playerId = +data.playerID;
                        injury.begin = moment(data.injuryBegin, 'DD.MM.YYYY');
                        injury.end = moment(data.injuryEnd, 'DD.MM.YYYY');
                        injury.type = data.injuryType;
                        injuries.push(injury);
                    }

                    return injuries;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getLiveLineup(match: number, team: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/lineup/' + match + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const lineup = new Lineup();
                    if (typeof json[0] !== 'undefined') {
                        lineup.formation = json[0].formation;
                    }
                    for (const data of json) {
                        let position = new Position();
                        position.player = +data.playerID;
                        position.position = +data.formationPosition;
                        position.substitution = data.substitution.toLowerCase() === 'true';
                        lineup.positions.push(position);
                    }

                    return lineup;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getLiveScore(match: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/ticker/score/' + match, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const score = new Score();
                    score.homeScore = +json.homeScore;
                    score.awayScore = +json.visitorScore;
                    return score;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getLiveTeamStatistic(match: number, team: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/statistics/team/' + match + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    let teamStatistic = new TeamStatistic();
                    teamStatistic.id = +json.id;
                    teamStatistic.stage = +json.stage;
                    teamStatistic.passesAccurate = +json.passesAccurate;
                    teamStatistic.possessionTimeTotal = json.possesionTimeTotal;
                    teamStatistic.shotsOutsideBox = +json.shotsOutsideBox;
                    teamStatistic.yellowCardsTotal = +json.yellowCardsTotal;
                    teamStatistic.shotsInsideBox = +json.shotsInsideBox;
                    teamStatistic.passesTotal = +json.passesTotal;
                    teamStatistic.passesPercent = json.passesPercent;
                    teamStatistic.foulsTotal = +json.foulsTotal;
                    teamStatistic.savesTotal = +json.savesTotal;
                    teamStatistic.shotsBlocked = +json.shotsBlocked;
                    teamStatistic.offSidesTotal = +json.offSidesTotal;
                    teamStatistic.shotsTotal = +json.shotsTotal;
                    teamStatistic.shotsOnGoal = +json.shotsOnGoal;
                    teamStatistic.cornersTotal = +json.cornersTotal;
                    teamStatistic.shotsOffGoal = +json.shotsOffGoal;
                    teamStatistic.redCardsTotal = +json.redCardsTotal;
                    teamStatistic.teamId = +json.teamID;
                    teamStatistic.matchDate = moment.utc(json.matchDate, 'DD.MM.YYYY HH:mm');
                    return teamStatistic;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getLivePlayersStatistics(match: number, team: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/statistics/players/' + match + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const livePlayerStatistics = [];
                    for (const data of json) {
                        const livePlayerStatistic = new LivePlayerStatistic();
                        livePlayerStatistic.id = +data.id;
                        livePlayerStatistic.nId = +data.nID;
                        livePlayerStatistic.yellowCards = +data.yellowCards;
                        livePlayerStatistic.playerId = +data.playerID;
                        livePlayerStatistic.goals = +data.goals;
                        livePlayerStatistic.shotsOnGoal = +data.shotsOnGoal;
                        livePlayerStatistic.shotsTotal = +data.shotsTotal;
                        livePlayerStatistic.passesTotal = +data.passesTotal;
                        livePlayerStatistic.passesAccurate = data.passesAccurate;
                        livePlayerStatistic.assists = +data.assists;
                        livePlayerStatistic.goalsConceded = +data.goalsConceded;
                        livePlayerStatistic.foulsDrawn = +data.foulsDrawn;
                        livePlayerStatistic.offSides = +data.offSides;
                        livePlayerStatistic.crossesTotal = +data.crossesTotal;
                        livePlayerStatistic.crossesAccurate = +data.crossesAccurate;
                        livePlayerStatistic.foulsCommitted = +data.foulsCommitted;
                        livePlayerStatistic.tackles = +data.tackles;
                        livePlayerStatistic.blocks = +data.blocks;
                        livePlayerStatistic.interceptions = +data.interceptions;
                        livePlayerStatistic.clearances = +data.clearances;
                        livePlayerStatistic.saves = +data.saves;
                        livePlayerStatistic.penaltyScore = +data.penaltyScore;
                        livePlayerStatistic.penaltyMissed = +data.penaltyMissed;
                        livePlayerStatistic.penaltySaved = +data.penaltySaved;
                        livePlayerStatistic.minutesPlayed = +data.minutesPlayed;
                    }

                    return livePlayerStatistics;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getPlayerStatistic(match: number, player: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'statistics/player/' + match + '/' + player, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    if (typeof json[0] === 'undefined') {
                        return null;
                    }

                    const playerStatistic = new PlayerStatistic();
                    playerStatistic.id = +json[0].id;
                    playerStatistic.yellowCards = +json[0].yellowCards;
                    playerStatistic.redCards = +json[0].redCards;
                    playerStatistic.playerId = +json[0].playerID;
                    playerStatistic.goals = +json[0].goals;
                    playerStatistic.minutesPlayed = +json[0].minutes;
                    playerStatistic.substitutionsOut = +json[0].substituteBench;
                    playerStatistic.substitutionsIn = +json[0].substituteIn;
                    playerStatistic.lineups = +json[0].lineUps;
                    playerStatistic.appearances = +json[0].appearences;
                    return playerStatistic;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getLivePlayerStatistic(match: number, player: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/statistics/player/' + match + '/' + player, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    if (typeof json[0] === 'undefined') {
                        return null;
                    }

                    const livePlayerStatistic = new LivePlayerStatistic();
                    livePlayerStatistic.id = +json[0].id;
                    livePlayerStatistic.nId = +json[0].nID;
                    livePlayerStatistic.yellowCards = +json[0].yellowCards;
                    livePlayerStatistic.playerId = +json[0].playerID;
                    livePlayerStatistic.goals = +json[0].goals;
                    livePlayerStatistic.shotsOnGoal = +json[0].shotsOnGoal;
                    livePlayerStatistic.shotsTotal = +json[0].shotsTotal;
                    livePlayerStatistic.passesTotal = +json[0].passesTotal;
                    livePlayerStatistic.passesAccurate = json[0].passesAccurate;
                    livePlayerStatistic.assists = +json[0].assists;
                    livePlayerStatistic.goalsConceded = +json[0].goalsConceded;
                    livePlayerStatistic.foulsDrawn = +json[0].foulsDrawn;
                    livePlayerStatistic.offSides = +json[0].offSides;
                    livePlayerStatistic.crossesTotal = +json[0].crossesTotal;
                    livePlayerStatistic.crossesAccurate = +json[0].crossesAccurate;
                    livePlayerStatistic.foulsCommitted = +json[0].foulsCommitted;
                    livePlayerStatistic.tackles = +json[0].tackles;
                    livePlayerStatistic.blocks = +json[0].blocks;
                    livePlayerStatistic.interceptions = +json[0].interceptions;
                    livePlayerStatistic.clearances = +json[0].clearances;
                    livePlayerStatistic.saves = +json[0].saves;
                    livePlayerStatistic.penaltyScore = +json[0].penaltyScore;
                    livePlayerStatistic.penaltyMissed = +json[0].penaltyMissed;
                    livePlayerStatistic.penaltySaved = +json[0].penaltySaved;
                    livePlayerStatistic.minutesPlayed = +json[0].minutesPlayed;
                    return livePlayerStatistic;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getNewPlayers(team: number, year: number) {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'team/newplayers/' + team + '/' + year, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const transfers = [];
                    for (const data of json) {
                        const transfer = new Transfer();
                        transfer.id = +data.id;
                        transfer.date = moment(data.transferDate, 'DD.MM.YYYY');
                        transfer.from = data.transferFrom;
                        transfer.to = data.transferTo;
                        transfer.value = data.transferValue;
                        transfer.playerId = +data.playerID;
                        transfer.firstName = data.firstName;
                        transfer.lastName = data.lastName;
                        transfer.position = data.position;
                        transfer.number = +data.number;

                      transfers.push(transfer);
                    }
                    return transfers;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getMissingPlayers(team: number, date: moment.Moment) {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'team/missingplayers/' + date.format('DD.MM.YYYY') + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const missingPlayers = [];
                    for (const data of json) {
                        const missingPlayer = new MissingPlayer();
                        missingPlayer.id = +data.id;
                        missingPlayer.playerId = +data.playerID;
                        missingPlayer.type = data.missingType;
                        missingPlayer.begin = moment(data.missingBegin, 'YYYY-MM-DD');
                        missingPlayer.end = moment(data.missingEnd, 'YYYY-MM-DD');
                        missingPlayers.push(missingPlayer);
                    }

                    return missingPlayers;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getPlayerPenaltyStatistic(league: number, player: number) {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'penalty/player/info/' + league + '/' + player, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    if (typeof json[0] === 'undefined') {
                        return null;
                    }

                    const penaltyStatistic = new PenaltyStatistic();
                    penaltyStatistic.id = +json[0].id;
                    penaltyStatistic.playerId = +json[0].playerID;
                    penaltyStatistic.total = +json[0].penTotal;
                    penaltyStatistic.noGoals = +json[0].penNoGoals;
                    penaltyStatistic.goals = +json[0].penGoals;
                    penaltyStatistic.quote = +json[0].penQuote;
                    return penaltyStatistic;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getTeamPenaltyStatistic(league: number, team: number) {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'penalty/team/info/' + league + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const penaltyStatistics = [];
                    for (const data of json) {
                        const penaltyStatistic = new PenaltyStatistic();
                        penaltyStatistic.id = +data.id;
                        penaltyStatistic.playerId = +data.playerID;
                        penaltyStatistic.total = +data.penTotal;
                        penaltyStatistic.noGoals = +data.penNoGoals;
                        penaltyStatistic.goals = +data.penGoals;
                        penaltyStatistic.quote = +data.penQuote;
                        penaltyStatistics.push(penaltyStatistic);
                    }

                    return penaltyStatistics;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getPlayerLeaguePenalties(league: number, player: number) {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'penalty/player/matches/' + league + '/' + player, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const penalties = [];
                    for (const data of json) {
                        const penalty = new Penalty();
                        penalty.id = +data.id;
                        penalty.date = moment(data.matchDate);
                        penalty.goal = data.goal.toLowerCase() === 'true';
                        penalty.adversaryTeamId = +data.adversaryTeamID;
                        penalty.adversaryTeamName = data.adversaryTeamName;
                        penalties.push(penalty);
                    }

                    return penalties;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getLeagueStanding(league: number, team: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/ticker/standing/' + league + '/' + team, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const standings = [];
                    for (const data of json) {
                        const standing = new Standing();
                        standing.position = +data.position;
                        standing.teamName = data.teamName;
                        standing.teamId = +data.teamID;
                        standing.gamesPlayed = +data.gamesPlayed;
                        standing.points = +data.points;
                        standing.goalDiff = data.goalDiff;
                        standings.push(standing);
                    }

                    return standings;
                }
            )
            .catch(this.handleError.bind(this));
    }

    getClock(match: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/ticker/timer/' + match, { search: params })
            .map(this.extractData)
            .map(
                json => {
                  const clock = new Clock();
                  clock.minutes = json.minutes;
                  clock.seconds = json.seconds;
                  clock.status  = json.status;
                  return clock;
                }
            )
    }

    getReferee(match: number) {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(this.apiBase + 'live/ticker/info/' + match, { search: params })
            .map(this.extractData)
            .map(
                json => {
                    const referee = new Referee();
                    referee.name = json.refereeName;
                    return referee;
                }
            )
            .catch(this.handleError.bind(this));
    }

    addPlayer(player: Player): Observable<Response> {
        const params = new URLSearchParams();
        params.set('token', this.token);
        params.set('userid', this.userId);

        return this.http.post(
            `${this.apiBase}players/${player.teamId}`,
            {
                teamID: player.teamId,
                number: player.number,
                birthDate: player.birthDate.format('DD/MM/YYYY'),
                firstName: player.firstName,
                lastName: player.lastName,
                playerName: player.name,
                nationality: player.nationality,
                birthCountry: player.birthCountry,
                birthPlace: player.birthPlace,
                weight: player.weight,
                height: player.height,
                position: player.position
            },
            { search: params }
        );
    }

    savePlayerPosition(player: Player, screenWidth: number, screenHeight: number): Observable<Response> {
        const params = new URLSearchParams(),
            posX = Math.round(player.x),
            posY = Math.round(player.y);
        params.set('token', this.token);
        params.set('userid', this.userId);
        return this.http.put(
            `${this.apiBase}player/position/${player.playerId}/${posX}/${posY}/${screenWidth}/${screenHeight}`,
            '',
            { search: params }
            );
    }

    savePlayerPositions(players: Player[], screenWidth: number, screenHeight: number): Observable<Response> {
        const requests: Observable<Response>[] = [];
        for (const player of players) {
            requests.push(this.savePlayerPosition(player, screenWidth, screenHeight));
        }
        return Observable.merge(requests).combineAll();
    }

    saveKeyfacts(player: Player): Observable<Response> {
        const params = new URLSearchParams();
        params.set('userid', this.userId);
        params.set('token', this.token);
        return this.http.put(`${this.apiBase}player/keyfacts/${player.playerId}`, {
            keyFacts: player.keyfacts
        }, {
            search: params
        });
    }

    getLiveGoals(matchId: number): Observable<Response> {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(`${this.apiBase}live/ticker/goals/${matchId}`, {
            search: params
        });
    }

    getLiveCards(matchId: number): Observable<Response> {
        const params = new URLSearchParams();
        params.set('token', this.token);
        return this.http.get(`${this.apiBase}live/ticker/cards/${matchId}`, {
            search: params
        });
    }

    playTeamSound(team: number) {
        const audio = new Audio();
        audio.src = this.soundFileBase + 'sound/teams/' + team + '.mp3';
        audio.load();
        audio.play();
    }

    playPlayerSound(player: number) {
        const audio = new Audio();
        audio.src = this.soundFileBase + 'sound/players/' + player + '.mp3';
        audio.load();
        audio.play();
    }
}



// WEBPACK FOOTER //
// /Users/mac/PlayGround/src/app/shared/data.service.ts